/**
 * sales_pipeline — every vertical, default OFF (api#1064). GET
 * /opportunities/pipeline, as sent: per open stage, the count, the weighted
 * value (`weightedValueKnownTotal`, api#1170), the known value and the
 * unvalued count. NO total across stages — the API returns none and
 * this widget adds no money (operator, 2026-10-14). Not the "Quote Pipeline"
 * widget, which counts quotes; neither reads the other's figures.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../api/client';
import { useLocale } from '../../../contexts/LocaleContext';
import { fmtCurrency } from '../../../lib/display-labels';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { STAGE_LABELS } from '../../../lib/opportunity';
import { OPEN_STAGES, type Pipeline } from '../../../types/opportunity';
import { LoadFailureNotice } from '../../ui/LoadFailureNotice';
import { WidgetFigure, WidgetPanel, WidgetPending } from '../WidgetPanel';

export default function SalesPipelineWidget() {
  const { currencyCode } = useLocale();
  const q = useQuery({
    queryKey: ['opportunities', 'pipeline', 'overview'],
    queryFn: () => api.opportunities.pipeline().then((r) => r.data.data as Pipeline),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = q.data === undefined ? queryLoadFailure(q) : null;
  const d = q.data;

  return (
    <WidgetPanel widgetKey="sales_pipeline" eyebrow="Opportunities" title="Sales Pipeline" link={{ label: 'Pipeline', to: '/sales/pipeline' }}>
      <div className="p-4">
        {failure ? (
          <LoadFailureNotice error={queryFailureError(q)} kind={failure} subject="the sales pipeline" className="py-6" />
        ) : !d ? (
          <WidgetPending rows={2} />
        ) : (
          <div className="grid grid-cols-3 gap-x-4 gap-y-5">
            {OPEN_STAGES.map((stage) => {
              const s = d.stages.find((x) => x.stage === stage);
              return (
                <WidgetFigure
                  key={stage}
                  testId={`sales-pipeline-${stage}`}
                  label={STAGE_LABELS[stage]}
                  value={s ? String(s.count) : '—'}
                  sub={s ? `${fmtCurrency(s.weightedValueKnownTotal, currencyCode)} weighted · ${fmtCurrency(s.valueKnownTotal, currencyCode)} known · ${s.valueUnknownCount} unvalued` : undefined}
                />
              );
            })}
          </div>
        )}
      </div>
    </WidgetPanel>
  );
}
